'use client';

// The pickers of the "Location" filter group. State stays in the sidebar;
// LocationFilterGroup hands its props through.
import { swissCantonLabel } from '@/lib/countries';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover } from '@/components/ui/popover';
import {
  FacetCount,
  FIELD_LABEL_CLASS,
  FIELD_PLACEHOLDER_CLASS,
  FilterLabel,
  INPUT_PLACEHOLDER_CLASS,
} from '../FilterControls';
import { deFold } from '../searchSidebarOptions';
import {
  ComboboxContent,
  ComboboxTrigger,
  FilterChip,
  OptionCheck,
} from './FilterFieldParts';
import type { LocationFilterGroupProps } from './LocationFilterGroup';

export function CityField({
  t,
  lockOf,
  cityOpen,
  setCityOpen,
  setCitySearch,
  staged,
  removeSet,
  set,
  citySearch,
  cityList,
  toggleSet,
  facetCount,
}: Pick<
  LocationFilterGroupProps,
  | 't'
  | 'lockOf'
  | 'cityOpen'
  | 'setCityOpen'
  | 'setCitySearch'
  | 'staged'
  | 'removeSet'
  | 'set'
  | 'citySearch'
  | 'cityList'
  | 'toggleSet'
  | 'facetCount'
>) {
  const { city: selectedCities } = staged;
  return (
    <div className="space-y-1.5">
      <FilterLabel lock={lockOf('city')} className={FIELD_LABEL_CLASS}>
        {t('search.sidebar.city')}
      </FilterLabel>
      <Popover
        open={lockOf('city') ? false : cityOpen}
        onOpenChange={(open) => {
          setCityOpen(open);
          if (!open) setCitySearch('');
        }}
      >
        <ComboboxTrigger disabled={!!lockOf('city')}>
          {selectedCities.length === 0 ? (
            <span className={FIELD_PLACEHOLDER_CLASS}>
              {t('search.sidebar.cityPlaceholder')}
            </span>
          ) : (
            selectedCities.map((c) => (
              <FilterChip
                key={c}
                onRemove={() => removeSet(c, set.city)}
              >
                <span className="max-w-[220px] truncate">{c}</span>
              </FilterChip>
            ))
          )}
        </ComboboxTrigger>
        <ComboboxContent>
          <Command shouldFilter={false}>
            <CommandInput
              className={INPUT_PLACEHOLDER_CLASS}
              placeholder={t('search.sidebar.searchCities', {
                defaultValue: 'Search cities...',
              })}
              value={citySearch}
              onValueChange={setCitySearch}
            />
            <CommandList className="max-h-[272px]">
              <CommandEmpty>
                {t('search.sidebar.noCityFound', {
                  defaultValue: 'No city found.',
                })}
              </CommandEmpty>
              <CommandGroup>
                {cityList
                  .filter(
                    (c) =>
                      !citySearch || deFold(c).includes(deFold(citySearch)),
                  )
                  .slice(0, 100)
                  .map((c) => (
                    <CommandItem
                      key={c}
                      value={c}
                      onSelect={() => toggleSet(c, set.city)}
                    >
                      <OptionCheck selected={selectedCities.includes(c)} />
                      <span className="truncate text-xs">{c}</span>
                      <FacetCount count={facetCount('city', c)} />
                    </CommandItem>
                  ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </ComboboxContent>
      </Popover>
    </div>
  );
}

/** Bundesland / canton; the label follows the country scope. */
export function BundeslandField({
  t,
  lockOf,
  staged,
  bundeslandOpen,
  setBundeslandOpen,
  i18n,
  removeSet,
  set,
  locationOptions,
  toggleSet,
  facetCount,
}: Pick<
  LocationFilterGroupProps,
  | 't'
  | 'lockOf'
  | 'staged'
  | 'bundeslandOpen'
  | 'setBundeslandOpen'
  | 'i18n'
  | 'removeSet'
  | 'set'
  | 'locationOptions'
  | 'toggleSet'
  | 'facetCount'
>) {
  const { country, Bundesland: selectedBundesland } = staged;
  return (
    <div className="space-y-1.5">
      <FilterLabel lock={lockOf('Bundesland')} className={FIELD_LABEL_CLASS}>
        {country === 'CH'
          ? t('search.sidebar.canton', { defaultValue: 'Canton' })
          : country === ''
            ? t('search.sidebar.stateOrCanton', {
                defaultValue: 'State / canton',
              })
            : t('search.sidebar.bundesland', {
                defaultValue: 'Federal state',
              })}
      </FilterLabel>
      <Popover
        open={lockOf('Bundesland') ? false : bundeslandOpen}
        onOpenChange={setBundeslandOpen}
      >
        <ComboboxTrigger disabled={!!lockOf('Bundesland')}>
          {selectedBundesland.length === 0 ? (
            <span className={FIELD_PLACEHOLDER_CLASS}>
              {t('search.sidebar.statePlaceholder', {
                defaultValue: 'e.g. Bavaria',
              })}
            </span>
          ) : (
            selectedBundesland.map((b) => (
              <FilterChip
                key={b}
                onRemove={() => removeSet(b, set.Bundesland)}
              >
                <span className="max-w-[220px] truncate">
                  {swissCantonLabel(b, i18n.language)}
                </span>
              </FilterChip>
            ))
          )}
        </ComboboxTrigger>
        <ComboboxContent>
          <Command>
            <CommandInput
              className={INPUT_PLACEHOLDER_CLASS}
              placeholder={t('search.sidebar.searchStates', {
                defaultValue: 'Search states...',
              })}
            />
            <CommandList>
              <CommandEmpty>
                {t('search.sidebar.noStateFound', {
                  defaultValue: 'No state found.',
                })}
              </CommandEmpty>
              <CommandGroup>
                {locationOptions.map(({ value: b, label }) => (
                  <CommandItem
                    key={b}
                    value={b}
                    keywords={[label]}
                    onSelect={() => toggleSet(b, set.Bundesland)}
                  >
                    <OptionCheck selected={selectedBundesland.includes(b)} />
                    <span className="truncate text-xs">{label}</span>
                    <FacetCount count={facetCount('bundesland', b)} />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </ComboboxContent>
      </Popover>
    </div>
  );
}
